import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  FolderTree,
  ShoppingCart,
  Users,
  Zap,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Palette,
  Box,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuthStore } from '../stores/authStore';

const menuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
  { icon: Package, label: 'Products', path: '/admin/products' },
  { icon: FolderTree, label: 'Categories', path: '/admin/categories' },
  { icon: ShoppingCart, label: 'Orders', path: '/admin/orders' },
  { icon: Users, label: 'Customers', path: '/admin/users' },
  { icon: Zap, label: 'Flash Sales', path: '/admin/flash-sales' },
  { icon: Palette, label: 'Colors', path: '/admin/colors' },
  { icon: Box, label: 'Sizes', path: '/admin/sizes' },
];

export function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/login');
    }
  }, [isAdmin, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const currentPage = menuItems.find((item) => item.path === location.pathname)?.label || 'Admin';

  return (
    <div className="dark admin-bg min-h-screen">
      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 bg-stone-900 border-b border-stone-800 px-4 py-3 flex items-center justify-between">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-stone-400"
          data-testid="admin-mobile-menu"
        >
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
        <span className="font-bold text-white">{currentPage}</span>
        <Link to="/" className="text-emerald-400 text-sm hover:underline">
          View Store
        </Link>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`admin-sidebar bg-stone-900 border-r border-stone-800 transition-transform duration-300 lg:translate-x-0 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="p-6">
            <Link to="/admin" className="block" data-testid="admin-logo">
              <h1 className="text-xl font-bold text-white">
                MEGA<span className="text-emerald-400">MART</span>
              </h1>
              <p className="text-xs text-stone-500 mt-1">Admin Panel</p>
            </Link>
          </div>

          <nav className="px-3 py-4">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-3 mb-1 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-emerald-900/50 text-emerald-400 border-l-2 border-emerald-400'
                      : 'text-stone-400 hover:text-white hover:bg-stone-800'
                  }`}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-stone-800">
            <div className="flex items-center gap-3 mb-4 px-2">
              <div className="w-10 h-10 bg-emerald-900 rounded-full flex items-center justify-center text-emerald-400 font-bold">
                {user?.name?.charAt(0) || 'A'}
              </div>
              <div className="flex-1 overflow-hidden">
                <p className="text-sm font-medium text-white truncate">{user?.name || 'Admin'}</p>
                <p className="text-xs text-stone-500 truncate">{user?.email}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Link to="/" className="flex-1">
                <Button variant="outline" size="sm" className="w-full text-stone-400 border-stone-700" data-testid="view-store-btn">
                  View Store
                </Button>
              </Link>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="text-stone-400 hover:text-red-400"
                data-testid="admin-logout"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="admin-content flex-1 p-4 lg:p-8">
          {/* Breadcrumb */}
          <div className="hidden lg:flex items-center gap-2 text-sm text-stone-500 mb-6">
            <Link to="/admin" className="hover:text-emerald-400">
              Admin
            </Link>
            {currentPage !== 'Dashboard' && (
              <>
                <ChevronRight className="h-4 w-4" />
                <span className="text-stone-300">{currentPage}</span>
              </>
            )}
          </div>

          {children}
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
